import type {
  Country,
  Locale,
  SalesforceLanguageCodeEnum,
} from '@/types/regionTypes';

import type { SelectOption } from '@/components/Select';
import type {
  ConfigurationCountryInfo,
  ConfigurationLanguageInfo,
} from '@/graphql/configuration/api';
import type { CountryCode, LanguageCodeEnum } from '@/graphql/saleor/api';
import { BRAND, ENVIRONMENT_NAME } from '@/lib/const';

import { DEFAULT_COUNTRY, DEFAULT_LANGUAGE } from '../regions';
import type { SitemapUrl } from '../sitemap/helpers/generic';
import countriesFallbackData from './fallback/countries.json';
import languagesFallbackData from './fallback/languages.json';
import countryOptionsForGallet from './gallet/countryOptions.json';
import languageOptionsForGallet from './gallet/languageOptions.json';

const aliasMap: Record<string, string> = {
  'en-US': 'enUS',
  'en-GB': 'enGB',
  'zh-Hant': 'zht',
};

export function formatLanguages(
  languages: ConfigurationLanguageInfo[],
): Record<string, Locale> {
  const results: Record<string, Locale> = {};
  languages.forEach((item: ConfigurationLanguageInfo) => {
    results[aliasMap[item.locale] ?? item.locale] = {
      locale: item.locale,
      code: item.code as LanguageCodeEnum,
      name: item.name,
      slug: item.slug,
      sfCode: item.sfCode as SalesforceLanguageCodeEnum,
    };
  });
  return results;
}

export function formatCountries(
  countriesInfo: ConfigurationCountryInfo[],
  languages: Record<string, Locale>,
): Country[] {
  const results: Country[] = [];

  countriesInfo.forEach((item: ConfigurationCountryInfo) => {
    results.push({
      favorite: item.favorite,
      continent: item.continent,
      channelSlug: item.channel ?? DEFAULT_COUNTRY.channelSlug,
      code: item.code as CountryCode,
      currency: item.currency ?? DEFAULT_COUNTRY.currency,
      defaultLocale: item.languages[0] ?? DEFAULT_LANGUAGE.locale,
      locales:
        item.languages.length > 0
          ? item.languages.map((item: string) => {
              return languages[aliasMap[item] ?? item];
            })
          : [DEFAULT_LANGUAGE],
      name: item.name,
      slug: item.code.toLowerCase(),
      region: item.region,
    });
  });

  return results;
}

interface LocaleConfigurationState {
  countries?: Country[];
  languages?: Record<string, Locale>;
}

export class LocaleConfiguration {
  private static singleton: LocaleConfiguration;
  public states: LocaleConfigurationState = {};

  constructor() {
    if (LocaleConfiguration.singleton) {
      return LocaleConfiguration.singleton;
    } else {
      LocaleConfiguration.singleton = this;
      return this;
    }
  }

  static getInstance() {
    if (LocaleConfiguration.singleton) {
      return LocaleConfiguration.singleton;
    } else {
      LocaleConfiguration.singleton = new LocaleConfiguration();
      return LocaleConfiguration.singleton;
    }
  }

  setStates(states: LocaleConfigurationState | null) {
    if (states) {
      this.states = states;
    }
  }

  getStates() {
    return this.states;
  }

  getLanguages() {
    return (
      this.states.languages ??
      formatLanguages(languagesFallbackData.data.languages)
    );
  }

  getSupportedCountries() {
    return (
      this.states.countries ??
      formatCountries(
        countriesFallbackData.data.countriesInfo,
        this.getLanguages(),
      )
    );
  }
}

export const isEnvProduction = () => {
  return ENVIRONMENT_NAME === 'PRD';
};

export enum Brand {
  Gallet = 'gallet',
  Breitling = 'breitling',
}

export function getBrandName(brand: Brand) {
  switch (brand) {
    case Brand.Gallet:
      return 'Gallet';
    default:
      return 'Breitling';
  }
}

export function toBrandEnum(brand: string): Brand {
  return Object.values(Brand)?.includes(brand as Brand)
    ? (brand as Brand)
    : Brand.Breitling;
}

type BrandConfigDetails = {
  dataPrivacyEmail: string;
  disableAlgoliaApiCall: boolean;
  disableSaleorApiCall: boolean;
  disallowedUrlPrefixes: string[];
  enableAccessibilityWidget: boolean;
  footerLinksPosition: 'left' | 'center';
  metaTags: {
    content: string;
    name: string;
  }[];
  newsletter?: {
    countryOptions: SelectOption[];
    guestApiUrl: string;
    languageOptions: SelectOption[];
    needPhone: boolean;
    reCaptchaBackgroundValidation: boolean;
    salutationDefaultValue: string;
  };
  newsletterGuestApiUrl?: string;
  newsletterGuestApiXApiKey?: string;
  newsletterNeedPhone?: boolean;
  newsletterReCaptchaBackgroundValidation?: boolean;
  oneTrustDataDomainScript: string;
  simpleFooter: boolean;
  simpleHeader: boolean;
  sitemap?: {
    getConstantUrls: (locale: string) => SitemapUrl[];
    root: {
      getAdditionalUrls: () => string;
    };
  };
  textAlignmentMobile: 'left' | 'center' | 'right';
};

const BrandConfigs: { [key: string]: BrandConfigDetails } = {
  Gallet: {
    disallowedUrlPrefixes: [
      'about',
      'chronolog',
      'collections',
      'ew',
      'my-account',
      'news',
      'newsletter',
      'notify-me',
      'search',
      'service',
      'store',
      'stores',
      'straps',
      'subscribe',
      'view-product',
      'watch-comparison',
      'watches',
      'wishlist',
      'contact/',
      'tracking/',
    ],
    simpleHeader: true,
    simpleFooter: true,
    footerLinksPosition: 'center',
    disableSaleorApiCall: true,
    disableAlgoliaApiCall: true,
    textAlignmentMobile: 'left',
    dataPrivacyEmail: 'dataprivacy@gallet.com',
    newsletter: {
      guestApiUrl: '/api/gallet/nl-subscribe/',
      needPhone: true,
      reCaptchaBackgroundValidation: true,
      countryOptions: countryOptionsForGallet,
      languageOptions: languageOptionsForGallet,
      salutationDefaultValue: '',
    },
    oneTrustDataDomainScript: isEnvProduction()
      ? '0195845c-b9e5-72f9-90cf-73fe3e46b282'
      : '0195845c-b9e5-72f9-90cf-73fe3e46b282-test',
    enableAccessibilityWidget: false,
    metaTags: [
      {
        name: 'msvalidate.01',
        content: 'D9A2DA7D61929490BD51C93B16C2FE57',
      },
    ],
  },
  Breitling: {
    disallowedUrlPrefixes: [],
    simpleHeader: false,
    simpleFooter: false,
    footerLinksPosition: 'left',
    disableSaleorApiCall: false,
    disableAlgoliaApiCall: false,
    textAlignmentMobile: 'center',
    dataPrivacyEmail: 'dataprivacy@breitling.com',
    enableAccessibilityWidget: true,
    oneTrustDataDomainScript: isEnvProduction()
      ? 'cddc5183-83e4-4159-91d5-e43119f86b64'
      : 'cddc5183-83e4-4159-91d5-e43119f86b64-test',
    sitemap: {
      root: {
        getAdditionalUrls: function () {
          return '';
        },
      },
      getConstantUrls: function (locale: string) {
        return [
          {
            lastUpdated: new Date().toISOString(),
            url: `/stores/breitling-boutiques/${locale.split('-')[0]}/`,
          },
        ];
      },
    },
    metaTags: [],
  },
};

export const getBrandConfig = () => {
  switch (BRAND) {
    case 'gallet':
      return BrandConfigs.Gallet;
    default:
      return BrandConfigs.Breitling;
  }
};

export const brandConfig = getBrandConfig();
